import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { Ticket, Trophy, Calendar, ChevronRight, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function ChallengeProgress() {
    const { language } = useLanguage();
    const [challenge, setChallenge] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCelebration, setShowCelebration] = useState(false);

    const t = {
        de: {
            title: 'Aktive Challenge',
            completed: 'Challenge geschafft!',
            day: 'Tag',
            of: 'von',
            noChallenge: 'Keine aktive Challenge',
            startChallenge: 'Starte eine Challenge im Wochenrückblick!',
            goal: 'Ziel',
            reward: 'Belohnung',
            congrats: 'Herzlichen Glückwunsch! Du hast die Challenge gemeistert!'
        },
        en: {
            title: 'Active Challenge',
            completed: 'Challenge Complete!',
            day: 'Day',
            of: 'of',
            noChallenge: 'No active challenge',
            startChallenge: 'Start a challenge in the Weekly Report!',
            goal: 'Goal',
            reward: 'Reward',
            congrats: 'Congratulations! You completed the challenge!'
        }
    }[language];

    useEffect(() => {
        loadChallenge();
    }, [language]);

    const loadChallenge = async () => {
        try {
            const response = await axios.get(`${API}/api/reports/challenge/active?language=${language}`);
            const data = response.data.active_challenge;
            setChallenge(data);
            if (data?.progress?.just_completed) {
                setShowCelebration(true);
                toast.success(`${t.completed} +${data.reward_points} XP`);
                setTimeout(() => setShowCelebration(false), 5000);
            }
        } catch (error) {
            console.error('Failed to load challenge:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading || !challenge) return null;

    const progress = challenge.progress || {};
    const isCompleted = challenge.completed || challenge.status === 'completed';

    return (
        <Card className={`overflow-hidden border-2 ${isCompleted ? 'border-green-400 dark:border-green-700' : 'border-amber-200 dark:border-amber-800/50'} bg-gradient-to-br ${isCompleted ? 'from-green-50/50 to-emerald-50/50 dark:from-green-950/20 dark:to-emerald-950/20' : 'from-amber-50/50 to-yellow-50/50 dark:from-amber-950/20 dark:to-yellow-950/20'}`} data-testid="challenge-progress">
            <CardContent className="p-4">
                {/* Celebration overlay */}
                <AnimatePresence>
                    {showCelebration && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 bg-green-500/10 backdrop-blur-sm z-10 flex items-center justify-center rounded-3xl"
                        >
                            <div className="text-center">
                                <PartyPopper className="w-12 h-12 text-green-500 mx-auto mb-2" />
                                <p className="font-bold text-lg">{t.completed}</p>
                                <p className="text-sm text-muted-foreground">+{challenge.reward_points} XP</p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isCompleted ? 'bg-green-500' : 'bg-gradient-to-br from-amber-400 to-orange-500'}`}>
                            {isCompleted ? <Trophy className="w-4 h-4 text-white" /> : <Ticket className="w-4 h-4 text-foreground" />}
                        </div>
                        <div>
                            <h3 className={`font-semibold text-sm ${isCompleted ? 'text-green-800 dark:text-green-200' : 'text-amber-900 dark:text-amber-100'}`}>
                                {isCompleted ? t.completed : t.title}
                            </h3>
                            <p className={`text-xs ${isCompleted ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                {isCompleted ? t.congrats : `${t.day} ${progress.days_elapsed} ${t.of} ${progress.days_total}`}
                            </p>
                        </div>
                    </div>
                    <Badge className={`${isCompleted ? 'bg-green-500 text-white' : 'bg-gradient-to-r from-amber-400 to-orange-500 text-foreground'} border-0`}>
                        {isCompleted ? '+' + challenge.reward_points + ' XP' : progress.emoji}
                    </Badge>
                </div>

                {/* Progress Bar */}
                <div className="mb-3">
                    <Progress
                        value={isCompleted ? 100 : (progress.percentage || 0)}
                        className={`h-2 ${isCompleted ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}
                    />
                </div>

                {/* Challenge Info */}
                <div className={`flex items-center gap-3 p-3 bg-white dark:bg-card rounded-xl border ${isCompleted ? 'border-green-200 dark:border-green-800' : 'border-amber-200 dark:border-amber-800'}`}>
                    <motion.div
                        className="text-3xl"
                        animate={isCompleted ? { rotate: [0, 15, -15, 0] } : { scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        {isCompleted ? '🎉' : progress.emoji}
                    </motion.div>
                    <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{challenge.goal}</p>
                        <p className="text-xs text-muted-foreground">{progress.message}</p>
                    </div>
                    {challenge.reward_points && (
                        <div className="text-right flex-shrink-0">
                            <div className={`flex items-center gap-1 ${isCompleted ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                <Trophy className="w-4 h-4" />
                                <span className="font-bold">+{challenge.reward_points}</span>
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
